import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { VoiceType, TaskSubmission } from '../types/api';
import { useToast } from '../hooks/useToast';
import { formatTime } from '../utils/format';

const STATUS_PRESET: Record<string, { label: string; tone: string }> = {
  pending: { label: '排队中', tone: 'muted' },
  generating_script: { label: '生成口播稿', tone: 'processing' },
  script_generated: { label: '脚本完成', tone: 'success' },
  synthesizing_audio: { label: '生成音频', tone: 'processing' },
  succeeded: { label: '已完成', tone: 'success' },
  completed: { label: '已完成', tone: 'success' },
  processing: { label: '处理中', tone: 'processing' },
  failed: { label: '失败', tone: 'error' },
};

function describeVoice(voice: VoiceType): string {
  const name = voice.display_name || voice.voice_name || '未命名音色';
  const tags: string[] = [];
  if (voice.gender === 'male') tags.push('男声');
  if (voice.gender === 'female') tags.push('女声');
  if (voice.language === 'zh') tags.push('中文');
  if (voice.language === 'en') tags.push('英文');
  return tags.length ? `${name}（${tags.join('/')}）` : name;
}

function isActiveStatus(status: string): boolean {
  return ['pending', 'generating_script', 'script_generated', 'synthesizing_audio', 'processing'].includes(status);
}

export default function TasksPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [tasks, setTasks] = useState<TaskSubmission[]>([]);
  const [voices, setVoices] = useState<VoiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingTaskId, setPlayingTaskId] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pollingRef = useRef<number | null>(null);

  const loadVoiceTypes = async () => {
    try {
      const response = await api.getVoiceTypes();
      setVoices(response.voice_types || []);
    } catch {
      console.error('加载音色失败');
    }
  };

  const loadTaskList = async () => {
    try {
      const response = await api.getTaskSubmissions();
      const enrichedTasks = (response.tasks || []).map((task) => {
        const statusMeta = STATUS_PRESET[task.status] || { label: task.status, tone: 'muted' };
        const voice = voices.find((v) => v.voice_type === task.voice_type);
        return {
          ...task,
          status_label: statusMeta.label,
          status_tone: statusMeta.tone,
          script_preview: (task.script_text || '').slice(0, 200),
          created_at_display: formatTime(task.created_at),
          finished_at_display: task.finished_at ? formatTime(task.finished_at) : '',
          voice_display_name: voice ? describeVoice(voice) : '未命名音色',
        };
      });
      setTasks(enrichedTasks);
    } catch {
      showToast('加载任务列表失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVoiceTypes();
  }, []);

  useEffect(() => {
    if (voices.length === 0) return;
    loadTaskList();
  }, [voices]);

  useEffect(() => {
    const hasActive = tasks.some((t) => isActiveStatus(t.status));
    if (hasActive) {
      pollingRef.current = window.setInterval(() => loadTaskList(), 8000);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [tasks]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } else {
        loadTaskList();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlayAudio = (task: TaskSubmission) => {
    if (!task.audio_url) return;

    if (playingTaskId === task.id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingTaskId(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(task.audio_url);
    audio.addEventListener('ended', () => setPlayingTaskId(null));
    audio.addEventListener('error', () => {
      showToast('播放失败', 'error');
      setPlayingTaskId(null);
    });
    audioRef.current = audio;
    setPlayingTaskId(task.id);
    audio.play().catch(() => {
      showToast('播放失败', 'error');
      setPlayingTaskId(null);
    });
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
          <p className="mt-3 text-muted">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container tasks-page">
      <div className="glass-card hero-card d-flex justify-content-between align-items-center">
        <div>
          <div className="hero-eyebrow text-fg-muted mb-1">查看任务</div>
          <h2 className="mb-2">统一任务中心</h2>
          <p className="text-fg-muted mb-0">实时查看任务状态与结果</p>
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="section-card">
          <div className="empty-placeholder">暂无任务，快去提交一个吧～</div>
        </div>
      ) : (
        <div className="task-card-list">
          {tasks.map((task) => (
            <div className="section-card task-card" key={task.id}>
              <div className="task-head">
                <div>
                  <div className="task-title">{task.title || '未命名任务'}</div>
                  <div className="task-meta">
                    <span>创建：{task.created_at_display || '-'}</span>
                    {task.finished_at_display && <span>完成：{task.finished_at_display}</span>}
                  </div>
                </div>
                <span className={`task-status ${task.status_tone || 'pending'}`}>
                  {task.status_label || task.status}
                </span>
              </div>
              <div className="task-meta">
                <span>类型：{task.style || '-'}</span>
                <span>音色：{task.voice_display_name || '-'}</span>
                {task.script_word_count && <span>字数：{task.script_word_count}</span>}
              </div>
              {task.status === 'failed' && task.error_message && (
                <div className="task-error">{task.error_message}</div>
              )}
              <div className="task-actions">
                <button className="detail-btn" type="button" onClick={() => navigate(`/task-detail/${task.id}`)}>
                  查看详情
                </button>
                {task.audio_url && (
                  <button
                    className="plain-btn"
                    type="button"
                    onClick={() => handlePlayAudio(task)}
                  >
                    {playingTaskId === task.id ? '停止播放' : '播放'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}